import * as Sentry from '@sentry/react';

/**
 * Error reporting, with PII scrubbed before anything leaves the browser.
 *
 * This app handles policy numbers, addresses, dates of birth and invite
 * tokens. An unscrubbed error report is a PII leak with extra steps, so the
 * scrubbing runs on every event rather than being a setting someone can flip
 * off by accident.
 *
 * Without `VITE_SENTRY_DSN` the SDK initializes disabled: no network, no
 * reporting, local dev and previews stay silent. The init call always runs —
 * an early return on a build-time-replaced env var lets the minifier prove
 * the whole branch dead and tree-shake the entire SDK out, which made
 * "zero sentry in the bundle" ambiguous between a failed deploy and a
 * missing env var. With the SDK always present, a deployed bundle proves
 * the wiring shipped; only the ingest host in it proves the DSN was set.
 */

// Order matters -- the token pattern must run before the generic long-string
// rules so an invite token is not partially matched first.
const REDACTIONS = [
  // invite tokens: 32 url-safe base64 bytes, as minted by create_client_invite
  [/\b[A-Za-z0-9_-]{40,}\b/g, '[redacted:token]'],
  // driver's license shapes: CA (1 letter + 7 digits) first, then a general
  // US shape. Over-matching an error code is an accepted cost on a form that
  // collects real license numbers.
  [/\b[A-Z]\d{7}\b/g, '[redacted:license]'],
  [/\b[A-Z]{1,2}\d{5,8}\b/g, '[redacted:license]'],
  // date-of-birth-shaped strings; before the phone rule, which would
  // otherwise claim the dashed form
  [/\b\d{2}[/-]\d{2}[/-]\d{4}\b/g, '[redacted:dob]'],
  [/[\w.+-]+@[\w-]+\.[\w.-]+/g, '[redacted:email]'],
  // phone numbers, formatted or not
  [/(\+?\d[\d\s().-]{8,}\d)/g, '[redacted:phone]'],
  // uuids -- policy, client and profile ids all travel as uuids
  [/\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/gi, '[redacted:id]'],
];

const scrub = (value) => {
  if (typeof value !== 'string') return value;
  return REDACTIONS.reduce((acc, [pattern, replacement]) => acc.replace(pattern, replacement), value);
};

const scrubDeep = (input, depth = 0) => {
  if (depth > 6 || input == null) return input;
  if (typeof input === 'string') return scrub(input);
  if (Array.isArray(input)) return input.map((v) => scrubDeep(v, depth + 1));
  if (typeof input === 'object') {
    const out = {};
    for (const [k, v] of Object.entries(input)) out[k] = scrubDeep(v, depth + 1);
    return out;
  }
  return input;
};

export const initErrorReporting = () => {
  const dsn = import.meta.env.VITE_SENTRY_DSN;

  Sentry.init({
    dsn: dsn || undefined,
    // enabled, not an early return: the guarantee (no DSN -> no network) is
    // identical, but the init call keeps the SDK referenced so the bundler
    // cannot tree-shake it away when the env var is unset at build time.
    enabled: Boolean(dsn),
    environment: import.meta.env.MODE,
    // No automatic PII: no IP address, no cookies, no request bodies.
    sendDefaultPii: false,
    // Errors only. No session replay and no performance tracing -- both would
    // capture the contents of a signed-in client's screen.
    tracesSampleRate: 0,

    beforeSend(event) {
      // Never identify the client. An error is actionable without knowing who
      // hit it, and "who" here is an insurance customer.
      delete event.user;
      if (event.request) {
        delete event.request.cookies;
        delete event.request.headers;
        delete event.request.data;
        // A query string can carry an invite token -- keep the path only.
        if (typeof event.request.url === 'string') {
          event.request.url = event.request.url.split('?')[0];
        }
      }
      if (event.message) event.message = scrub(event.message);
      if (event.exception?.values) {
        event.exception.values = event.exception.values.map((v) => ({
          ...v,
          value: scrub(v.value),
        }));
      }
      if (event.extra) event.extra = scrubDeep(event.extra);
      if (event.breadcrumbs) {
        event.breadcrumbs = event.breadcrumbs.map((b) => ({
          ...b,
          message: scrub(b.message),
          data: scrubDeep(b.data),
        }));
      }
      return event;
    },

    beforeBreadcrumb(breadcrumb) {
      // Drop the breadcrumb types that exist to record what the user typed or
      // fetched; neither is worth a PII risk on this app.
      if (breadcrumb.category === 'console') return null;
      if (breadcrumb.category === 'ui.input') return null;
      if (breadcrumb.data?.url) breadcrumb.data.url = String(breadcrumb.data.url).split('?')[0];
      return breadcrumb;
    },
  });
};
